import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getPlaceholderImageUrl, normalizeApiAssetPath } from '../../utils/api'

const HOMEPAGE_UNIT_TYPES = ['4 Sleeper Unit', '6 Sleeper Unit'] as const

const normalizeHomepageImage = (value: unknown, fallbackValue: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return fallbackValue
  }

  return normalizeApiAssetPath(value)
}

const sanitizeUnitDescriptions = (units: unknown): UnitDescription[] => {
  if (!Array.isArray(units)) {
    return []
  }

  return units.filter((unit): unit is UnitDescription => {
    return Boolean(
      unit &&
      typeof unit === 'object' &&
      'unitType' in unit &&
      typeof (unit as UnitDescription).unitType === 'string' &&
      HOMEPAGE_UNIT_TYPES.includes((unit as UnitDescription).unitType as (typeof HOMEPAGE_UNIT_TYPES)[number])
    )
  })
}

export const defaultHomepageSettings: HomepageSettings = {
  heroTitle: 'Experience Paradise at Marina Glen Holiday Resort',
  heroSubtitle: 'Luxury Meets Nature\'s Beauty',
  heroDescription: 'Immerse yourself in world-class amenities, breathtaking ocean views, and exceptional service. Your perfect coastal getaway awaits with pristine beaches and unforgettable experiences.',
  featuresTitle: 'Why Choose Marina Glen Holiday Resort?',
  featuresSubtitle: 'We offer unparalleled luxury and ocean-inspired experiences to make your stay unforgettable',
  featureCards: [
    {
      id: 'ocean-views',
      title: 'Ocean Views',
      description: 'Wake up to stunning ocean vistas from your private balcony. Every room offers breathtaking panoramic views of the endless blue horizon.',
      emoji: '🌊',
      image: getPlaceholderImageUrl()
    },
    {
      id: 'fine-dining',
      title: 'Fine Dining',
      description: 'Savor exquisite coastal cuisine prepared by world-renowned chefs. Multiple restaurants featuring fresh seafood and international flavors.',
      emoji: '🍽️',
      image: getPlaceholderImageUrl()
    },
    {
      id: 'spa-wellness',
      title: 'Spa & Wellness',
      description: 'Rejuvenate your mind and body at our award-winning oceanfront spa. Professional treatments and wellness programs with ocean-inspired therapy.',
      emoji: '🧘',
      image: getPlaceholderImageUrl()
    }
  ],
  accommodationTitle: 'Accommodation',
  accommodationSubtitle: 'Choose from our collection of elegantly designed units with stunning ocean views',
  unitDescriptions: [
    {
      unitType: '4 Sleeper Unit',
      title: '4 Sleeper Unit',
      description: 'Comfortable units perfect for small families or groups of friends.',
      image: getPlaceholderImageUrl()
    },
    {
      unitType: '6 Sleeper Unit',
      title: '6 Sleeper Unit',
      description: 'Spacious units perfect for larger families and groups.',
      image: getPlaceholderImageUrl()
    },
  ],
  ctaTitle: 'Ready for Your Dream Ocean Vacation?',
  ctaDescription: 'Book your stay today and experience the ultimate in luxury and relaxation by the ocean'
}

export const sanitizeHomepageSettings = (value: unknown): HomepageSettings => {
  const candidate = value && typeof value === 'object' ? value as Record<string, unknown> : {}

  return {
    ...defaultHomepageSettings,
    ...candidate,
    featureCards: Array.isArray(candidate.featureCards)
      ? candidate.featureCards.map((card, index) => {
          const source = card && typeof card === 'object' ? card as Record<string, unknown> : {}
          const fallback = defaultHomepageSettings.featureCards[index] || defaultHomepageSettings.featureCards[0]
          return {
            id: typeof source.id === 'string' && source.id.trim() ? source.id.trim() : fallback.id,
            title: typeof source.title === 'string' && source.title.trim() ? source.title.trim() : fallback.title,
            description: typeof source.description === 'string' && source.description.trim() ? source.description.trim() : fallback.description,
            emoji: typeof source.emoji === 'string' && source.emoji.trim() ? source.emoji.trim() : fallback.emoji,
            image: normalizeHomepageImage(source.image, fallback.image || ''),
          }
        })
      : defaultHomepageSettings.featureCards,
    unitDescriptions: sanitizeUnitDescriptions(candidate?.unitDescriptions).length > 0
      ? sanitizeUnitDescriptions(candidate?.unitDescriptions).map((unit, index) => ({
          ...unit,
          image: normalizeHomepageImage(unit.image, defaultHomepageSettings.unitDescriptions[index]?.image || ''),
        }))
      : defaultHomepageSettings.unitDescriptions
  }
}

export interface UnitDescription {
  unitType: string
  title: string
  description: string
  image?: string
}

export interface FeatureCard {
  id: string
  title: string
  description: string
  emoji: string
  image?: string
}

export interface HomepageSettings {
  heroTitle: string
  heroSubtitle: string
  heroDescription: string
  featuresTitle: string
  featuresSubtitle: string
  featureCards: FeatureCard[]
  accommodationTitle: string
  accommodationSubtitle: string
  unitDescriptions: UnitDescription[]
  ctaTitle: string
  ctaDescription: string
}

interface HomepageState {
  settings: HomepageSettings
  loading: boolean
  error: string | null
}

const initialState: HomepageState = {
  settings: defaultHomepageSettings,
  loading: true,
  error: null
}

const homepageSlice = createSlice({
  name: 'homepage',
  initialState,
  reducers: {
    updateHomepageSettings: (state, action: PayloadAction<Partial<HomepageSettings>>) => {
      const nextSettings = { ...state.settings, ...action.payload }
      state.settings = sanitizeHomepageSettings(nextSettings)
    },
    setHomepageSettings: (state, action: PayloadAction<HomepageSettings>) => {
      state.settings = sanitizeHomepageSettings(action.payload)
      state.loading = false
      state.error = null
    },
    updateUnitDescription: (state, action: PayloadAction<UnitDescription>) => {
      if (!HOMEPAGE_UNIT_TYPES.includes(action.payload.unitType as (typeof HOMEPAGE_UNIT_TYPES)[number])) {
        return
      }
      const index = state.settings.unitDescriptions.findIndex(unit => unit.unitType === action.payload.unitType)
      if (index !== -1) {
        state.settings.unitDescriptions[index] = action.payload
      }
    },
    updateFeatureCard: (state, action: PayloadAction<FeatureCard>) => {
      const index = state.settings.featureCards.findIndex(card => card.id === action.payload.id)
      if (index !== -1) {
        state.settings.featureCards[index] = action.payload
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    }
  }
})

export const {
  updateHomepageSettings,
  setHomepageSettings,
  updateUnitDescription,
  updateFeatureCard,
  setLoading,
  setError
} = homepageSlice.actions

export default homepageSlice.reducer